import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  onApprove: () => void;
  onReject: (reason: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

export function CaseReportReviewerCard({ onApprove, onReject, isApproving, isRejecting }: Props) {
  const reasonId = useId();
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 500;

  return (
    <section className="border-accent-ink/8 rounded-2xl border bg-white p-5 shadow-xs">
      <h3 className="font-display tracking-eyebrow text-accent-ink text-[11px] font-medium uppercase italic">
        Review
      </h3>
      {rejectMode ? (
        <div className="mt-4 flex flex-col gap-2">
          <label htmlFor={reasonId} className="text-accent-ink text-[13px] font-medium">
            Reason for rejection
          </label>
          <Textarea
            id={reasonId}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={3}
            aria-required
            aria-invalid={reason !== '' && !valid}
            placeholder="Explain what needs to change before resubmission."
            className="border-accent-ink/15 focus-visible:border-primary-300 resize-y text-[13.5px] leading-relaxed placeholder:text-[12.5px] placeholder:leading-snug placeholder:font-normal placeholder:text-neutral-400"
          />
          <div className="flex items-center justify-between gap-2">
            <p
              className={`font-mono text-[11px] tabular-nums ${
                trimmed.length > 500 ? 'text-error-500' : 'text-neutral-400'
              }`}
            >
              {`${String(trimmed.length)} / 500`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRejectMode(false);
                  setReason('');
                }}
                disabled={isRejecting}
                className="rounded-full px-3"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  onReject(trimmed);
                }}
                disabled={!valid || isRejecting}
                className="shadow-error-500/15 hover:shadow-error-500/20 rounded-full px-4 shadow-sm transition-shadow hover:shadow-md"
              >
                Submit rejection
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={onApprove}
            disabled={isApproving || isRejecting}
            className="shadow-primary-700/10 hover:shadow-primary-700/15 flex-1 rounded-full px-4 shadow-sm transition-shadow hover:shadow-md"
          >
            Approve
          </Button>
          <Button
            variant="outline-destructive"
            size="sm"
            onClick={() => {
              setRejectMode(true);
            }}
            disabled={isApproving || isRejecting}
            className="flex-1 rounded-full px-4"
          >
            Reject
          </Button>
        </div>
      )}
    </section>
  );
}
